// Raw markdown, resolved at build time.
//
// `eager: true` inlines every page into the bundle rather than emitting 43 lazy
// chunks. The whole corpus is ~350KB of text that gzips to a fraction of that,
// and eager loading means a doc page has no loading state, no waterfall, and no
// failure mode where a chunk 404s after a deploy — which matters more here than
// bundle size, because these URLs are quoted inside customer ERP installs.

const RAW = import.meta.glob(
  // The directory's own README is the editing guide for these files. It is for
  // whoever edits them, not for partners, so it is excluded rather than inlined
  // into the bundle as an unreachable page.
  ['../../content/**/*.md', '!../../content/README.md'],
  {
    query: '?raw',
    import: 'default',
    eager: true,
  }
) as Record<string, string>

const CONTENT_PREFIX = '../../content/'

// Keys come back as glob-relative paths; the manifest names files relative to
// `content/`. Normalise once here so nothing downstream knows about either.
const BY_FILE: Record<string, string> = Object.fromEntries(
  Object.entries(RAW).map(([key, value]) => [
    key.startsWith(CONTENT_PREFIX) ? key.slice(CONTENT_PREFIX.length) : key,
    value,
  ])
)

// Frontmatter is still in the files — it is how the pages were authored, and
// dropping it would make the diff against curri-docs unreadable. Titles come
// from the manifest instead, so the only thing needed at render time is to not
// print it.
const FRONTMATTER = /^---\r?\n[\s\S]*?\r?\n---\r?\n?/

export const stripFrontmatter = (markdown: string): string =>
  markdown.replace(FRONTMATTER, '').replace(/^\s*\n/, '')

/**
 * Raw markdown for a manifest `file` path, frontmatter removed.
 *
 * Throws rather than returning empty: a manifest entry naming a file that does
 * not exist is a build-time mistake, and a blank page is a much worse way to
 * find out than a thrown error in the one place that can explain it.
 */
export const contentFor = (file: string): string => {
  const raw = BY_FILE[file]
  if (raw === undefined) {
    throw new Error(
      `No markdown found for manifest entry "${file}". Expected content/${file}.`
    )
  }
  return stripFrontmatter(raw)
}

/** Every file the glob found, for the manifest-coverage test. */
export const contentFiles = (): string[] => Object.keys(BY_FILE).sort()

/**
 * Markdown with its frontmatter still attached.
 *
 * The frontmatter is no longer read at render time, but it is still the record
 * of how each page was configured on docs.curri.com — so the tests read it to
 * check the manifest against it rather than against a hand-written list.
 */
export const rawContentFor = (file: string): string => BY_FILE[file] ?? ''
